import React, { forwardRef, ReactNode } from 'react';

import { MenuGroups } from 'V2/VarSelect';

import { useCustomOptionStyle, useSelectStyle } from './styles';
import Select, { SelectProps } from '../Select';
import MenuItem from '../MenuItem';
import Checkbox from '../Checkbox';
import ListSubheader from '../ListSubheader';
import Typography from '../Typography';
import { useMUITheme } from '../styles/theme';
import { cx, css } from '../../customEmotion';

export type Option = {
  optionType: 'subject' | 'var' | 'option';
  children?: string | ReactNode;
  value: string | number;
};

interface MultiVarSelectProps extends SelectProps {
  /**
   * Data structure required by MultiVarSelect
   */
  options?: Option[];
  groups?: MenuGroups[];
  value: Array<string | number>;
  disabledOptions?: Array<string | number>; // Array of values
}
/**
 * @deprecated 由於 MultiVarSelect 模改原先的 Select 太多，rc 元件應保持簡單，因此 deprecated 並且在 admin src/components/organisms/MultiSelect 建立一多選下拉選單元件
 * 在選項互斥仍有使用到此元件，因此不刪除而用 deprecated 的方式避免已上線的功能異常
 */
const MultiVarSelect = forwardRef<HTMLDivElement, MultiVarSelectProps>(
  (props, ref) => {
    const customOptionStyle = useCustomOptionStyle();
    const selectStyle = useSelectStyle();
    const theme = useMUITheme();

    const {
      options,
      groups,
      value,
      disabledOptions,
      placeholder,
      onChange,
      variant = 'outlined',
      onOpen,
      onClose,
      ...rest
    } = props;

    return (
      <Select
        {...rest}
        ref={ref}
        value={value}
        onChange={onChange}
        variant={variant}
        onOpen={onOpen}
        onClose={onClose}
        fullWidth
        multiple
        classes={selectStyle}
      >
        {groups &&
          groups.map(group => {
            return [
              <ListSubheader style={{ pointerEvents: 'none' }}>
                {group.groupTitle}
              </ListSubheader>,
              group.options.map(opt => {
                if (opt.emptyLabel) {
                  return (
                    <MenuItem style={{ pointerEvents: 'none' }}>
                      <Typography
                        variant="body2"
                        className={css`
                          color: ${theme.palette.grey[500]};
                        `}
                      >
                        {opt.emptyLabel}
                      </Typography>
                    </MenuItem>
                  );
                }
                return (
                  <MenuItem
                    className={customOptionStyle.menuItem}
                    value={opt.value}
                    key={opt.value}
                    disabled={
                      disabledOptions &&
                      disabledOptions.includes(opt.value as string | number)
                    }
                  >
                    <Checkbox
                      color="primary"
                      checked={value.includes(opt.value as string | number)}
                      className={customOptionStyle.checkbox}
                    />
                    <div
                      className={cx(customOptionStyle.root, {
                        [customOptionStyle.var]: group.optionType === 'var',
                      })}
                    >
                      {opt.children}
                    </div>
                  </MenuItem>
                );
              }),
            ];
          })}
        {options &&
          options.map(opt => (
            <MenuItem
              className={customOptionStyle.menuItem}
              value={opt.value}
              key={opt.value}
              disabled={disabledOptions && disabledOptions.includes(opt.value)}
            >
              <Checkbox
                color="primary"
                checked={value.includes(opt.value)}
                className={customOptionStyle.checkbox}
              />
              <div
                className={cx(customOptionStyle.root, {
                  [customOptionStyle.var]: opt.optionType === 'var',
                })}
              >
                {opt.children}
              </div>
            </MenuItem>
          ))}
      </Select>
    );
  }
);

MultiVarSelect.displayName = 'MultiVarSelect';

export default MultiVarSelect;
